import { useCallback, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

const SESSION_KEY = "visitor_session_id";
const CONSENT_KEY = "cookie_consent";

interface PageEvent {
  event_type: string;
  event_category?: string;
  element_selector?: string;
  element_text?: string;
  event_data?: Record<string, any>;
  x_position?: number;
  y_position?: number;
  scroll_position?: number;
  viewport_width?: number;
  viewport_height?: number;
}

const hasAnalyticsConsent = (): boolean => {
  try {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) return false;
    const parsed = JSON.parse(consent);
    return parsed.analytics === true;
  } catch {
    return false;
  }
};

const hasMarketingConsent = (): boolean => {
  try {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) return false;
    const parsed = JSON.parse(consent);
    return parsed.marketing === true;
  } catch {
    return false;
  }
};

// Track events via rate-limited edge function
const trackEventsViaEdge = async (events: PageEvent[], sessionId: string) => {
  try {
    const { error } = await supabase.functions.invoke('track-analytics', {
      body: {
        type: 'page_events',
        data: {
          events: events.map(event => ({
            session_id: sessionId,
            page_path: window.location.pathname,
            event_type: event.event_type,
            event_category: event.event_category || null,
            element_selector: event.element_selector || null,
            element_text: event.element_text || null,
            event_data: event.event_data || {},
            x_position: event.x_position || null,
            y_position: event.y_position || null,
            scroll_position: event.scroll_position || null,
            viewport_width: event.viewport_width || null,
            viewport_height: event.viewport_height || null,
          })),
        },
      },
    });

    if (error) {
      console.error('Error tracking events via edge:', error);
    }
  } catch (error) {
    console.error('Error tracking events via edge:', error);
  }
};

export const usePageEvents = () => {
  const scrollDepthRef = useRef(0);
  const maxScrollDepthRef = useRef(0);
  const pageLoadTimeRef = useRef(Date.now());
  const eventsQueueRef = useRef<PageEvent[]>([]);
  const flushTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track event
  const trackEvent = useCallback(async (event: PageEvent) => {
    if (!hasAnalyticsConsent()) return;

    const sessionId = sessionStorage.getItem(SESSION_KEY);
    if (!sessionId) return;

    const fullEvent = {
      ...event,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      scroll_position: window.scrollY,
    };

    eventsQueueRef.current.push(fullEvent);

    // Debounce flush
    if (flushTimeoutRef.current) {
      clearTimeout(flushTimeoutRef.current);
    }
    flushTimeoutRef.current = setTimeout(() => {
      flushEvents();
    }, 2000);
  }, []);

  // Flush events to database via edge function
  const flushEvents = async () => {
    if (eventsQueueRef.current.length === 0) return;

    const sessionId = sessionStorage.getItem(SESSION_KEY);
    if (!sessionId) return;

    const events = [...eventsQueueRef.current];
    eventsQueueRef.current = [];

    try {
      await trackEventsViaEdge(events, sessionId);
    } catch (error) {
      console.error("Error flushing page events:", error);
    }
  };

  // Track click
  const trackClick = useCallback((e: MouseEvent) => {
    if (!hasMarketingConsent()) return;
    
    const target = e.target as HTMLElement;

    const classNameValue = (() => {
      const cn: unknown = (target as any)?.className;
      if (!cn) return "";
      if (typeof cn === "string") return cn;
      // SVG elements often have SVGAnimatedString
      if (typeof (cn as any)?.baseVal === "string") return (cn as any).baseVal as string;
      return String(cn);
    })();

    const selector =
      target.tagName.toLowerCase() +
      (target.id ? `#${target.id}` : "") +
      (classNameValue ? `.${classNameValue.trim().split(/\s+/).join(".")}` : "");

    
    trackEvent({
      event_type: 'click',
      event_category: target.tagName.toLowerCase(),
      element_selector: selector.substring(0, 255),
      element_text: (target.innerText || target.textContent || '').substring(0, 100),
      x_position: e.clientX,
      y_position: e.clientY,
    });
  }, [trackEvent]);

  // Track scroll depth
  const trackScrollDepth = useCallback(() => {
    if (!hasAnalyticsConsent()) return;

    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrolled = window.scrollY;
    const depth = Math.round((scrolled / scrollHeight) * 100);
    
    scrollDepthRef.current = depth;
    
    if (depth > maxScrollDepthRef.current) {
      maxScrollDepthRef.current = depth;
      
      // Track milestones: 25%, 50%, 75%, 100%
      const milestones = [25, 50, 75, 100];
      for (const milestone of milestones) {
        if (depth >= milestone && scrollDepthRef.current < milestone) {
          trackEvent({
            event_type: 'scroll_milestone',
            event_category: 'engagement',
            event_data: { milestone, actual_depth: depth },
          });
        }
      }
    }
  }, [trackEvent]);

  // Get current scroll depth
  const getScrollDepth = useCallback(() => maxScrollDepthRef.current, []);

  // Get time on page
  const getTimeOnPage = useCallback(() => 
    Math.floor((Date.now() - pageLoadTimeRef.current) / 1000), 
  []);

  // Track form interactions
  const trackFormInteraction = useCallback((formId: string, action: 'start' | 'submit' | 'abandon') => {
    if (!hasMarketingConsent()) return;
    
    trackEvent({
      event_type: 'form_interaction',
      event_category: 'form',
      element_selector: formId,
      event_data: { action },
    });
  }, [trackEvent]);

  // Track outbound links
  const trackOutboundLink = useCallback((url: string) => {
    if (!hasAnalyticsConsent()) return;
    
    trackEvent({
      event_type: 'outbound_link',
      event_category: 'link',
      event_data: { url },
    });
  }, [trackEvent]);

  // Track file downloads
  const trackDownload = useCallback((filename: string, filetype: string) => {
    if (!hasAnalyticsConsent()) return;
    
    trackEvent({
      event_type: 'download',
      event_category: 'file',
      event_data: { filename, filetype },
    });
  }, [trackEvent]);

  // Track video interactions
  const trackVideoInteraction = useCallback((videoId: string, action: 'play' | 'pause' | 'complete', progress?: number) => {
    if (!hasMarketingConsent()) return;
    
    trackEvent({
      event_type: 'video_interaction',
      event_category: 'video',
      element_selector: videoId,
      event_data: { action, progress },
    });
  }, [trackEvent]);

  // Setup event listeners
  useEffect(() => {
    if (!hasAnalyticsConsent()) return;

    // Reset on page load
    pageLoadTimeRef.current = Date.now();
    maxScrollDepthRef.current = 0;

    // Scroll tracking
    let scrollTimeout: ReturnType<typeof setTimeout>;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(trackScrollDepth, 100);
    };

    // Click tracking (only with marketing consent)
    const handleClick = (e: MouseEvent) => {
      if (hasMarketingConsent()) {
        trackClick(e);
      }
    };

    // Visibility change tracking
    const handleVisibilityChange = () => {
      if (document.hidden) {
        trackEvent({
          event_type: 'visibility_change',
          event_category: 'engagement',
          event_data: { visible: false, time_on_page: getTimeOnPage() },
        });
      } else {
        trackEvent({
          event_type: 'visibility_change',
          event_category: 'engagement',
          event_data: { visible: true },
        });
      }
    };

    // Before unload - flush events and track exit
    const handleBeforeUnload = () => {
      trackEvent({
        event_type: 'page_exit',
        event_category: 'engagement',
        event_data: { 
          scroll_depth: maxScrollDepthRef.current,
          time_on_page: getTimeOnPage()
        },
      });
      flushEvents();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    if (hasMarketingConsent()) {
      document.addEventListener('click', handleClick);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('click', handleClick);
      
      if (flushTimeoutRef.current) {
        clearTimeout(flushTimeoutRef.current);
      }
    };
  }, [trackScrollDepth, trackClick, trackEvent, getTimeOnPage]);

  return {
    trackEvent,
    trackClick,
    trackFormInteraction,
    trackOutboundLink,
    trackDownload,
    trackVideoInteraction,
    getScrollDepth,
    getTimeOnPage,
    flushEvents,
  };
};

export default usePageEvents;
